import React, { useEffect, useState, useMemo } from "react";
import { createTheme, useTheme } from "@mui/material/styles";
import { Link as RouterLink, useNavigate, useParams, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import WorkIcon from "@mui/icons-material/Work";
import {
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Box,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Chip
} from "@mui/material";

import { getJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getJobOrderLogsById } from "../API/jobOrderLogsAPI";

import CircleIcon from "@mui/icons-material/Circle";
import BadgeIcon from "@mui/icons-material/Badge";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { 
  JOB_TYPES,
  JOB_DESCRIPTIONS,
  DOCUMENTS_TO_BRING,
  EQUIPMENT
} from "./utils/JobOrderConstants";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

// LOGS 1) Helper function to format job type strings
const formatJobType = (typeStr) => {
  if (!typeStr) return '';
  return typeStr
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to flatten nested object from updated_fields
const flattenObject = (obj, parentKey = "", result = {}) => {
  for (const [key, value] of Object.entries(obj)) {
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    // 1) If it's an array…
    if (Array.isArray(value)) {
      // 1a) Empty array → emit as a leaf
      if (value.length === 0) {
        result[newKey] = [];
      } else {
        // 1b) Otherwise, iterate each element
        value.forEach((item, index) => {
          if (item !== null && typeof item === "object") {
            flattenObject(item, `${newKey}[${index}]`, result);
          } else {
            result[`${newKey}[${index}]`] = item;
          }
        });
      }

    // 2) If it's a nested object (not null)…
    } else if (value !== null && typeof value === "object") {
      // Recurse normally (if nested object is empty, recursion will immediately return no sub-keys)
      flattenObject(value, newKey, result);

    // 3) Primitive → emit value
    } else {
      result[newKey] = value;
    }
  }

  return result;
};


function ViewJobOrder() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [client, setClient] = useState({})
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPayrollReports = searchParams.get('from') === 'payroll-reports';
  const returnTo = searchParams.get('returnTo');

  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({
    address: "",
    client: {
      name: "",
      address: {
        id: "",
        area: "",
      },
      contact: {
        id: "",
        email: {
          id: "",
        },
        number: {
          id: "",
        },
      },
    },
    job_types: [],
    job_description: [],
    competencies: [],
    specialization: {
      general_pest_control: [],
      termite: [],
    },
    job_date: "",
    start_time: "",
    end_time: "",
    technicians: [],
    endorser_name: "",
    endorser_contact_number: "",
    current_status: "",
    documents_to_bring: [],
    payment_method: "",
    schedule_confirmed: "",
    client_complaints: "",
    equipment: [],
    form_type: "",
    appointment_type: "",
    treatment_type: "",
  }); // State to store the job order
  const [loading, setLoading] = useState(true); // State to manage loading status
  const [error, setError] = useState(null); // State to manage error messages
  const [logs, setLogs] = useState([]);


  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        const data = await getJobOrder(id); // Call the API
        const client = await getClient(data.client_id);  // Fetch client using client_id from job order
        setClient(client);
        setJobOrder(data); // Set the job order data
      } catch (err) {
        setError("Failed to fetch job order"); // Set error message
      } finally {
        setLoading(false); // Stop loading
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, [id]); // Dependency array ensures the effect runs when ID changes


  useEffect(() => {
    const fetchJobOrderLogs = async () => {
      try {
        const logData = await getJobOrderLogsById(id);
        setLogs(logData);
      } catch (error) {
        console.error("Failed to fetch job order logs:", error);
      }
    };

    fetchJobOrderLogs();
  }, [id]);

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedDocuments = DOCUMENTS_TO_BRING
    .filter((label) => (jobOrder.documents_to_bring || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedEquipment = EQUIPMENT
    .filter((label) => (jobOrder.equipment || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));


  // LOGS 2) Sort logs in descending order by timestamp_update (newest first)
  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      const tA = a.timestamp_update ? new Date(a.timestamp_update) : 0;
      const tB = b.timestamp_update ? new Date(b.timestamp_update) : 0;
      return tB - tA;
    });
  }, [logs]);

  // LOGS 3) Pagination state & calculations
  const [page, setPage] = useState(1);
  const ENTRIES_PER_PAGE = 3;
  const totalPages = Math.ceil(sortedLogs.length / ENTRIES_PER_PAGE);

  // LOGS 4) Slice out only the logs for the current page
  const paginatedLogs = useMemo(() => {
    const startIndex = (page - 1) * ENTRIES_PER_PAGE;
    return sortedLogs.slice(startIndex, startIndex + ENTRIES_PER_PAGE);
  }, [sortedLogs, page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // LOGS 5) toggle state (default: hidden)
  const [showLogs, setShowLogs] = useState(false);
  const toggleLogs = () => setShowLogs((prev) => !prev);


  return (
    <BaseFormWrapper
      titleIcon={<WorkIcon />}
      title="Operations"
      subTitle="Manage Job Orders"
      subTitleRoute="/manage-job-orders"
      formTitle="View Job Order"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form className="job-order-form">
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Client Information
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Job Order ID row */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Job Order ID"
                      value={id || ""}
                      size="small"
                      fullWidth
                      disabled
                    />
                  </Grid>

                  {/* Client Name + Client ID + Work Permit chip */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Client Name"
                          variant="outlined"
                          fullWidth
                          value={jobOrder?.client?.name || ""}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Client ID"
                          variant="outlined"
                          fullWidth
                          value={jobOrder?.client?.id || ""}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md="auto" sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                        {client?.work_permit_required ? (
                          <Chip
                            icon={<BadgeIcon sx={{ fontSize: 18 }} />}
                            label="WORK PERMIT REQUIRED"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "rgba(25, 118, 210, 0.6)",
                              color: "#1976d2",
                              bgcolor: "rgba(25, 118, 210, 0.08)",
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                              height: 30,
                            }}
                          />
                        ) : null}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={10}>
                        <TextField
                          fullWidth
                          label="Job Site Address"
                          value={
                              [
                                jobOrder.client.address?.house_unit_street || "",
                                jobOrder.client.address?.barangay           || "",
                                jobOrder.client.address?.city               || "",
                                jobOrder.client.address?.metro_province     || ""
                              ]
                                .filter(part => part)             
                                .join(", ")                        
                              + (jobOrder.client.address?.zipcode
                                  ? ` ${jobOrder.client.address.zipcode}`
                                  : ""
                                )
                            }
                          InputProps={{
                            disabled: true,
                          }}
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder.client.address?.area || ""}
                          fullWidth
                          disabled
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={`${jobOrder.client.contact?.firstname} ${jobOrder.client.contact?.lastname}`.trim()}
                      InputProps={{
                        disabled: true,
                      }}
                    />

                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      value={jobOrder.client.contact?.email?.email || ""}
                      InputProps={{
                        disabled: true,
                      }}
                    />

                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      value={`${jobOrder.client.contact?.number?.type || ''}: ${jobOrder.client.contact?.number?.number || ''}`.trim()}
                      InputProps={{
                        disabled: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Category"
                      variant="outlined"
                      value={client?.category || ''}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Parent Client"
                      variant="outlined"
                      value={client?.parent_client?.name || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="subtitle2">Job Type</Typography>} sx={{ pb: 0 }} />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {displayedJobTypes.map((option, idx) => (
                    <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>          
          
          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="subtitle2">Job Description</Typography>} sx={{ pb: 0 }} />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {displayedJobDescriptions.map((option, idx) => (
                    <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography

                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Schedule Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                  >
                    Schedule
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={2}>
                    <TextField
                      label="Date"
                      name="job_date"
                      type="date"
                      value={jobOrder.job_date || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Start Time"
                      name="start_time"
                      type="time"
                      value={jobOrder.start_time || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="End Time"
                      name="end_time"
                      type="time"
                      value={jobOrder.end_time}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase", fontSize: "1rem" }}>
                    Appointment
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">Appointment Type</InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        value={jobOrder?.appointment_type || ""}
                        label="Appointment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">Treatment Type</InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        value={jobOrder?.treatment_type || ""}
                        label="Treatment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Endorsement Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Typography>
                    Endorsement Form
                  </Typography>
                }
                sx={{ padding: 0 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Name"
                      name="endorser_name"
                      type="text"
                      value={jobOrder.endorser_name || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Endorser Contact Number"
                      name="endorser_contact_number"
                      type="text"
                      value={jobOrder.endorser_contact_number || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Current Status"
                      name="current_status"
                      value={jobOrder.current_status || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="heavily_infested">Heavily Infested</MenuItem>
                      <MenuItem value="medium_infestation">Medium Infestation</MenuItem>
                      <MenuItem value="light_infestation">Light Infestation</MenuItem>
                      <MenuItem value="for_inspection_first_before_treament">
                        For Inspection First Before Treament
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Payment Method"
                      name="payment_method"
                      value={jobOrder.payment_method || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="COD">COD</MenuItem>
                      <MenuItem value="For Billing">For Billing</MenuItem>
                      <MenuItem value="Check collection">Check collection</MenuItem>
                      <MenuItem value="Bank deposit">Bank deposit</MenuItem>
                      <MenuItem value="Online/Gcash">Online/Gcash</MenuItem>
                      <MenuItem value="Paymongo">Paymongo</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Schedule Confirmed with Clients"
                      name="schedule_confirmed"
                      value={jobOrder.schedule_confirmed || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="With Client Complaints"
                      name="client_complaints"
                      value={jobOrder.client_complaints || ""}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      disabled
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        
          {/* Required Documents Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="subtitle2">Required Documents to Bring</Typography>} sx={{ pb: 0 }} />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {displayedDocuments.map((option, idx) => (
                    <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Equipment Section */}          
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader title={<Typography variant="subtitle2">Equipment to Bring</Typography>} sx={{ pb: 0 }} />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {displayedEquipment.map((option, idx) => (
                    <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Technicians Section */}
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Technicians
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {jobOrder.technicians.length > 0 ? (
                        jobOrder.technicians.map(
                          (technician, index) => (
                            <Grid item xs={12} key={technician.id}>
                              <TextField
                                label={`Technician ${index + 1}`}
                                variant="outlined"
                                fullWidth
                                value={`${technician.firstname} ${technician.lastname}`}
                                disabled
                              />
                            </Grid>
                          )
                        )
                      ) : (
                        <Grid item xs={12}>
                          <Typography
                            variant="body1"
                            color="textSecondary"
                          >
                            No technicians selected.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Type Section */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="h6">Form Type</Typography>}
                sx={{ padding: 0 }}
              />
              <CardContent>
                <TextField
                  select
                  label="Form Type"
                  name="form_type"
                  value={jobOrder.form_type || ""}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled    // ← always disabled now
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="makati_gpc_form">Makati GPC Form</MenuItem>
                  <MenuItem value="qc_form">QC Form</MenuItem>
                  <MenuItem value="termite_control_form">Termite Control Form</MenuItem>
                  <MenuItem value="transmittal_form">Transmittal Form</MenuItem>
                </TextField>
              </CardContent>
            </Card>
          </Grid>


          {/* ========== LOGS SECTION ========== */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={null}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={toggleLogs}
                  >
                    {showLogs ? "Hide Logs" : "Show Logs"}
                  </Button>
                }
                sx={{
                  px: 2,
                  py: 1,
                }}
              />
              {showLogs && (
                <CardContent sx={{ px: 2, py: 1 }}>
                  {sortedLogs.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No logs available.
                    </Typography>
                  ) : (
                    <Box sx={{ width: "100%" }}>
                      {/* ---------- HEADER ROW ---------- */}
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          fontWeight: "bold",
                          borderBottom: "1px solid #e0e0e0",
                          pb: 1,
                          px: 2,
                        }}
                      >
                        <Box sx={{ width: "10%", textAlign: "left" }}>USER</Box>
                        <Box sx={{ width: "15%", textAlign: "left" }}>TIMESTAMP</Box>
                        <Box sx={{ width: "37.5%", textAlign: "left" }}>PREVIOUS</Box>
                        <Box sx={{ width: "37.5%", textAlign: "left" }}>UPDATE</Box>
                      </Box>

                      {/* ---------- PAGINATED LOG ROWS ---------- */}
                      {paginatedLogs.map((log, index) => {
                        // Get previous log entry for comparison
                        const allLogsSorted = sortedLogs;
                        const currentLogIndex = allLogsSorted.findIndex(l => l === log);
                        const previousLog = currentLogIndex < allLogsSorted.length - 1 ? allLogsSorted[currentLogIndex + 1] : null;
                        
                        // Flatten both current and previous updated_fields
                        // const currentFlatMap = flattenObject(log.updated_fields || {});
                        // const previousFlatMap = previousLog ? flattenObject(previousLog.updated_fields || {}) : {};
                        
                        // 1) Flatten updated_fields for the current log
                        const rawFlatMap = flattenObject(log.updated_fields || {});

                        // 2) Start with previous log's flat map (for backward compatibility)
                        let previousFlatMap = previousLog
                          ? flattenObject(previousLog.updated_fields || {})
                          : {};

                        // 3) Build normalized maps:
                        //    - Keys ending with ".old" → PREVIOUS column
                        //    - Keys ending with ".new" → UPDATE column
                        //    - Other keys behave as before (new value only)
                        const currentFlatMap = {};

                        Object.entries(rawFlatMap).forEach(([fullKey, val]) => {
                          if (fullKey.endsWith(".new")) {
                            const baseKey = fullKey.replace(/\.new$/, "");
                            currentFlatMap[baseKey] = val;
                          } else if (fullKey.endsWith(".old")) {
                            const baseKey = fullKey.replace(/\.old$/, "");
                            previousFlatMap[baseKey] = val;
                          } else {
                            currentFlatMap[fullKey] = val;
                          }
                        });

                        // Now we have two flat objects:
                        // previousFlatMap → old values (either from this log's ".old" or from the previous log)
                        // currentFlatMap  → new values (from ".new" or plain values)

                        return (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              width: "100%",
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              p: 2,
                              mt: 1,
                            }}
                          >
                            {/* USER */}
                            <Box sx={{ width: "10%" }}>
                              <Typography sx={{ fontSize: "0.875rem" }}>{log.user_update || ""}</Typography>
                            </Box>

                            {/* TIMESTAMP */}
                            <Box sx={{ width: "15%" }}>
                              <Typography sx={{ fontSize: "0.875rem" }}>
                                {log.timestamp_update
                                  ? new Date(log.timestamp_update).toLocaleString("en-US", {
                                      month: "2-digit",
                                      day: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    })
                                  : ""}
                              </Typography>
                            </Box>

                            {/* PREVIOUS */}
                            <Box sx={{ width: "37.5%" }}>
                              <Box component="div" sx={{ whiteSpace: "pre-wrap", fontSize: "0.875rem", wordBreak: "break-word" }}>
                                {(() => {
                                  // Categorize fields
                                  const categories = {
                                    "CLIENT INFORMATION": [],
                                    "JOB TYPE": [],
                                    "JOB DESCRIPTION": [],
                                    "SPECIAL NOTES": [],
                                    "SCHEDULE": [],
                                    "APPOINTMENT": [],
                                    "ENDORSEMENT FORM": [],
                                    "REQUIRED DOCUMENTS TO BRING": [],
                                    "EQUIPMENT TO BRING": [],
                                    "TECHNICIANS": [],
                                    "FORM TYPE": []
                                  };

                                  Object.entries(currentFlatMap).forEach(([fullKey, val]) => {
                                    const lowerKey = fullKey.toLowerCase();
                                    if (lowerKey.includes("client") || lowerKey.includes("address") || lowerKey.includes("contact") || lowerKey.includes("category") || lowerKey.includes("parent") || lowerKey.includes("location_type")) {
                                      categories["CLIENT INFORMATION"].push([fullKey, val]);
                                    } else if (lowerKey.includes("job_types") || lowerKey === "job_types") {
                                      categories["JOB TYPE"].push([fullKey, val]);
                                    } else if (lowerKey.includes("job_description")) {
                                      categories["JOB DESCRIPTION"].push([fullKey, val]);
                                    } else if (lowerKey.includes("special_notes")) {
                                      categories["SPECIAL NOTES"].push([fullKey, val]);
                                    } else if (lowerKey.includes("job_date") || lowerKey.includes("start_time") || lowerKey.includes("end_time")) {
                                      categories["SCHEDULE"].push([fullKey, val]);
                                    } else if (lowerKey.includes("appointment_type") || lowerKey.includes("treatment_type")) {
                                      categories["APPOINTMENT"].push([fullKey, val]);
                                    } else if (lowerKey.includes("endorser") || lowerKey.includes("current_status") || lowerKey.includes("payment_method") || lowerKey.includes("schedule_confirmed") || lowerKey.includes("client_complaints")) {
                                      categories["ENDORSEMENT FORM"].push([fullKey, val]);
                                    } else if (lowerKey.includes("documents_to_bring")) {
                                      categories["REQUIRED DOCUMENTS TO BRING"].push([fullKey, val]);
                                    } else if (lowerKey.includes("equipment")) {
                                      categories["EQUIPMENT TO BRING"].push([fullKey, val]);
                                    } else if (lowerKey.includes("technician")) {
                                      categories["TECHNICIANS"].push([fullKey, val]);
                                    } else if (lowerKey.includes("form_type")) {
                                      categories["FORM TYPE"].push([fullKey, val]);
                                    }
                                  });

                                  return Object.entries(categories).map(([category, fields], idx) => {
                                    if (fields.length === 0) return null;
                                    return (
                                      <Box key={category} sx={{ mt: idx > 0 ? 2 : 0 }}>
                                        <Box sx={{ fontWeight: "bold", mb: 0.5 }}>{category}</Box>
                                        {fields.sort(([aKey], [bKey]) => aKey.localeCompare(bKey)).map(([fullKey, val]) => (
                                          <Box key={fullKey}>
                                            <span style={{ fontWeight: 600 }}>{fullKey}:</span>{" "}
                                            "{previousFlatMap[fullKey] !== undefined ? (Array.isArray(previousFlatMap[fullKey]) ? previousFlatMap[fullKey].join(", ") : String(previousFlatMap[fullKey])) : "N/A"}"
                                          </Box>
                                        ))}
                                      </Box>
                                    );
                                  });
                                })()}
                              </Box>
                            </Box>

                            {/* UPDATE */}
                            <Box sx={{ width: "37.5%" }}>
                              <Box component="div" sx={{ whiteSpace: "pre-wrap", fontSize: "0.875rem", wordBreak: "break-word" }}>
                                {(() => {
                                  // Categorize fields
                                  const categories = {
                                    "CLIENT INFORMATION": [],
                                    "JOB TYPE": [],
                                    "JOB DESCRIPTION": [],
                                    "SPECIAL NOTES": [],
                                    "SCHEDULE": [],
                                    "APPOINTMENT": [],
                                    "ENDORSEMENT FORM": [],
                                    "REQUIRED DOCUMENTS TO BRING": [],
                                    "EQUIPMENT TO BRING": [],
                                    "TECHNICIANS": [],
                                    "FORM TYPE": []
                                  };

                                  Object.entries(currentFlatMap).forEach(([fullKey, val]) => {
                                    const lowerKey = fullKey.toLowerCase();
                                    if (lowerKey.includes("client") || lowerKey.includes("address") || lowerKey.includes("contact") || lowerKey.includes("category") || lowerKey.includes("parent") || lowerKey.includes("location_type")) {
                                      categories["CLIENT INFORMATION"].push([fullKey, val]);
                                    } else if (lowerKey.includes("job_types") || lowerKey === "job_types") {
                                      categories["JOB TYPE"].push([fullKey, val]);
                                    } else if (lowerKey.includes("job_description")) {
                                      categories["JOB DESCRIPTION"].push([fullKey, val]);
                                    } else if (lowerKey.includes("special_notes")) {
                                      categories["SPECIAL NOTES"].push([fullKey, val]);
                                    } else if (lowerKey.includes("job_date") || lowerKey.includes("start_time") || lowerKey.includes("end_time")) {
                                      categories["SCHEDULE"].push([fullKey, val]);
                                    } else if (lowerKey.includes("appointment_type") || lowerKey.includes("treatment_type")) {
                                      categories["APPOINTMENT"].push([fullKey, val]);
                                    } else if (lowerKey.includes("endorser") || lowerKey.includes("current_status") || lowerKey.includes("payment_method") || lowerKey.includes("schedule_confirmed") || lowerKey.includes("client_complaints")) {
                                      categories["ENDORSEMENT FORM"].push([fullKey, val]);
                                    } else if (lowerKey.includes("documents_to_bring")) {
                                      categories["REQUIRED DOCUMENTS TO BRING"].push([fullKey, val]);
                                    } else if (lowerKey.includes("equipment")) {
                                      categories["EQUIPMENT TO BRING"].push([fullKey, val]);
                                    } else if (lowerKey.includes("technician")) {
                                      categories["TECHNICIANS"].push([fullKey, val]);
                                    } else if (lowerKey.includes("form_type")) {
                                      categories["FORM TYPE"].push([fullKey, val]);
                                    }
                                  });

                                  return Object.entries(categories).map(([category, fields], idx) => {
                                    if (fields.length === 0) return null;
                                    return (
                                      <Box key={category} sx={{ mt: idx > 0 ? 2 : 0 }}>
                                        <Box sx={{ fontWeight: "bold", mb: 0.5 }}>{category}</Box>
                                        {fields.sort(([aKey], [bKey]) => aKey.localeCompare(bKey)).map(([fullKey, val]) => (
                                          <Box key={fullKey}>
                                            <span style={{ fontWeight: 600 }}>{fullKey}:</span>{" "}
                                            "{Array.isArray(val) ? val.join(", ") : String(val)}"
                                          </Box>
                                        ))}
                                      </Box>
                                    );
                                  });
                                })()}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                      
                      {/* ---------- PAGINATION CONTROLS ---------- */}
                      {totalPages > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                          }}
                        >
                          <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              )}
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              onClick={() => {
                if (returnTo) {
                  // Use window.location.href to force a full page navigation
                  window.location.href = returnTo;
                } else if (fromPayrollReports) {
                  navigate("/payroll-reports");
                } else {
                  navigate("/manage-job-orders");
                }
              }}
              disabled={isLoading}
            >
              {fromPayrollReports ? "Back to Payroll Reports" : "Back"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default ViewJobOrder;
